<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import BaseButton from '@/components/Base/BaseButton.vue'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { ChevronDownIcon } from '@heroicons/vue/20/solid'
import { useI18n } from 'vue-i18n'

import type { CountryStores, GameContest } from '@/types'

const props = withDefaults(
  defineProps<{
    conditionsUrl?: string | null
    backendError?: string
    availableCountries?: CountryStores[]
    isSubmit?: boolean
    firstnameInputFocus: boolean
    locale?: string
    contestGame: GameContest
  }>(),
  {
    conditionsUrl: null,
    backendError: '',
    locale: 'fr',
    availableCountries: () => [],
    isSubmit: false,
  },
)

const emit = defineEmits(['submit', 'focus-input'])

const route = useRoute()
const { locale: i18nLocale } = useI18n()

const refFirstName = ref(null)
const isLoaded = ref(false)
const localStorageCustomer = JSON.parse(localStorage.getItem('customer') || '{}')

const email = ref(localStorageCustomer.email || '')
const error = ref('')
const firstName = ref(localStorageCustomer.firstName || '')
const optin = ref(false)
const phone = ref(localStorageCustomer.phoneNumber || '')
const countryCode = ref('FR')

const isDisabled = computed(() => {
  if (props.isSubmit) {
    return true
  }
  return (
    email.value === '' || phone.value === '' || firstName.value === '' || countryCode.value === ''
  )
})

const computedCountryLabel = computed(() => {
  return props.availableCountries.filter(
    (country) => country.country.alpha2Code == countryCode.value,
  )[0]?.country?.alpha2Code
})

const removeError = () => {
  emit('focus-input', '')
  error.value = ''
}

const checkEmail = () => {
  const regexEmail =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return regexEmail.test(String(email.value).toLowerCase())
}

const checkValidation = () => {
  if (!checkEmail()) {
    error.value = 'Veuillez rentrer un e-mail valide'
    return false
  }
  return true
}

const submit = () => {
  if (checkValidation()) {
    const payload = {
      phoneNumber: phone.value,
      country: countryCode.value ?? 'FR',
      email: email.value,
      firstName: firstName.value,
      hasOptin: optin.value,
    }

    localStorage.setItem(
      'customer',
      JSON.stringify({
        email: payload.email,
        firstName: payload.firstName,
        phoneNumber: payload.phoneNumber,
      }),
    )

    emit('submit', payload)
  }
}

watch(
  () => props.firstnameInputFocus,
  (newVal) => {
    if (newVal) {
      if (refFirstName.value) refFirstName.value.focus()
    }
  },
)

onMounted(() => {
  countryCode.value = props.availableCountries[0]?.country?.alpha2Code ?? 'FR'
  isLoaded.value = true

  if (route.query.locale) {
    i18nLocale.value = (route.query.locale as string) || props.locale
  }
})
</script>
<template>
  <form id="form" @submit.prevent="submit">
    <div class="mb-4">
      <input
        id="firstName"
        ref="refFirstName"
        v-model="firstName"
        class="input-style"
        name="firstName"
        :placeholder="$t('fields.firstname')"
        required
        type="text"
        @focus="removeError"
      />
    </div>

    <div class="mb-4">
      <div class="mt-1 flex gap-2">
        <Menu
          v-if="isLoaded && availableCountries.length > 1"
          as="div"
          class="relative z-10 inline-block text-left"
        >
          <div class="h-full">
            <MenuButton
              class="inline-flex h-full w-full items-center justify-center border border-gray-300 bg-white py-2 pl-4 pr-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100"
            >
              {{ computedCountryLabel }}
              <ChevronDownIcon class="ml-2 h-5 w-5 text-gray-700" />
            </MenuButton>
          </div>
          <MenuItems
            class="absolute left-0 w-56 origin-top-left divide-y divide-gray-100 bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
          >
            <div class="py-1">
              <div class="max-h-96 overflow-auto">
                <MenuItem v-for="(countryItem, index) in availableCountries" :key="index" as="div">
                  <button
                    role="menuitem"
                    :class="[
                      countryCode === countryItem.country.alpha2Code
                        ? 'bg-gray-100 text-gray-900'
                        : 'text-gray-700',
                      'block w-full px-4 py-2 text-left text-sm focus:outline-none',
                      'flex w-full items-center px-4 py-2 text-left text-sm hover:bg-gray-100 hover:text-gray-900 focus:outline-none',
                    ]"
                    @click="countryCode = countryItem.country.alpha2Code"
                  >
                    <img
                      :src="countryItem.country.flag"
                      class="mr-3 h-3 w-4 object-contain"
                      :alt="countryItem.country.name"
                    />
                    {{ countryItem.country.name }} -
                    {{ countryItem.country.alpha2Code }}
                  </button>
                </MenuItem>
              </div>
            </div>
          </MenuItems>
        </Menu>
        <input
          id="phone"
          ref="ref-phone"
          v-model="phone"
          class="input-style"
          maxlength="15"
          minlength="9"
          name="phone"
          :placeholder="$t('fields.phoneNumber')"
          required
          size="20"
          type="tel"
          @focus="removeError"
        />
      </div>
    </div>
    <div>
      <input
        id="email"
        ref="ref-email"
        v-model="email"
        class="input-style"
        name="email"
        :placeholder="$t('fields.email')"
        required
        type="email"
        @focus="removeError"
      />
      <p class="p-2 text-red-600">
        {{ error }}
      </p>
    </div>
    <p v-if="backendError" class="pt-2 text-red-600">
      {{ $t(backendError, i18nLocale) }}
    </p>

    <div class="relative my-4 flex items-start">
      <div class="flex h-10 items-center">
        <input
          id="optin"
          v-model="optin"
          name="optin"
          type="checkbox"
          :checked="true"
          class="h-4 w-4 rounded border-gray-300 checked:bg-cyan-500 checked:ring-2 checked:ring-cyan-500"
        />
        <div class="ml-3 text-sm">
          <label for="optin" class="font-medium">
            {{ contestGame.optinText }}
          </label>
        </div>
      </div>
    </div>

    <base-button :disabled="isDisabled" is-active :loader="isSubmit" type="submit" class="w-full">
      {{ contestGame.formSubmitButtonText }}
    </base-button>
    <p v-if="conditionsUrl !== null" class="pt-2 text-center text-xs italic">
      {{ $t('termsOfService.beforeLink') }}
      <a :href="conditionsUrl" target="_blank" class="underline">{{ $t('termsOfService.link') }}</a>
      {{ $t('termsOfService.afterLink') }}
    </p>
  </form>
</template>
